import { useMemo, useState, useCallback } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { formatAmount } from '@/lib/utils'
import type { Transaction } from '@/lib/hooks'
import { DayCell, buildDayData } from './day-cell'
import type { Variant } from './day-cell'
import { DailySpendingSheet } from './daily-spending-sheet'

interface SpendingCalendarProps {
  transactions: Transaction[]
  currentMonth: string // "YYYY-MM"
  showSparkline?: boolean
  variant?: Variant
  weeks: { start: Date; end: Date }[]
}

const DAY_LABELS = ['月', '火', '水', '木', '金', '土', '日'] as const

export function SpendingCalendar({ transactions, currentMonth, variant = 'dots', weeks }: SpendingCalendarProps) {
  // 日別支出の集計
  const dailyTotals = useMemo(() => {
    const map = new Map<string, number>()
    for (const tx of transactions) {
      map.set(tx.date, (map.get(tx.date) ?? 0) + tx.total_amount)
    }
    return map
  }, [transactions])

  // ヒートマップ着色用の四分位境界 (Q1/Q2/Q3)。
  // 単純な max ベースのスケーリングだと家賃などの単発の大型支出に
  // 引っ張られて通常日が level 1 に潰れるため、件数を 4 バケットに
  // 等分する quartile 方式にして外れ値の影響を境界に出さない。
  const heatmapQuartiles = useMemo<readonly [number, number, number] | null>(() => {
    const positives: number[] = []
    for (const amount of dailyTotals.values()) {
      if (amount > 0) positives.push(amount)
    }
    if (positives.length === 0) return null
    positives.sort((a, b) => a - b)
    const q = (p: number) =>
      positives[Math.min(positives.length - 1, Math.floor(positives.length * p))]
    return [q(0.25), q(0.5), q(0.75)]
  }, [dailyTotals])

  // 日別カテゴリカラーの集計（カテゴリごとに1色、重複排除）
  const dailyCategoryColors = useMemo(() => {
    const map = new Map<string, string[]>()
    for (const tx of transactions) {
      const colors = map.get(tx.date) ?? []
      if (!map.has(tx.date)) map.set(tx.date, colors)
      for (const item of tx.items) {
        const color = item.parent_category_color
        if (color && !colors.includes(color)) {
          colors.push(color)
        }
      }
    }
    return map
  }, [transactions])

  const [monthYear, monthNum] = useMemo(() => currentMonth.split('-').map(Number), [currentMonth])
  const todayStr = useMemo(() => {
    const now = new Date()
    const y = now.getFullYear()
    const m = String(now.getMonth() + 1).padStart(2, '0')
    const d = String(now.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }, [])

  // 月モード: 全日のカレンダーデータ
  const monthDays = useMemo(() => {
    return weeks.flatMap((week) =>
      buildDayData(week.start, 7, dailyTotals, dailyCategoryColors, monthYear, monthNum, todayStr)
    )
  }, [weeks, dailyTotals, dailyCategoryColors, monthYear, monthNum, todayStr])

  // 月合計
  const monthTotal = useMemo(() => {
    return monthDays
      .filter((d) => d.isCurrentMonth)
      .reduce((sum, d) => sum + d.amount, 0)
  }, [monthDays])

  // BottomSheet state for daily detail
  const [selectedDateStr, setSelectedDateStr] = useState<string | null>(null)
  const sheetOpen = selectedDateStr !== null

  const handleDayClick = useCallback((dateStr: string) => {
    setSelectedDateStr(dateStr)
  }, [])

  const selectedDayTransactions = useMemo(() => {
    if (!selectedDateStr) return []
    return transactions.filter((tx) => tx.date === selectedDateStr)
  }, [transactions, selectedDateStr])

  return (
    <>
    <Card>
      <CardHeader>
        <CardTitle>支出</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Day Labels */}
        <div className={"grid grid-cols-7 gap-1 text-center text-[10px] text-muted-foreground"}>
          {DAY_LABELS.map((label) => (
            <div key={label}>{label}</div>
          ))}
        </div>

        {/* Month Calendar Grid */}
        <div className={"grid grid-cols-7 gap-1"}>
          {monthDays.map((day) => (
            <DayCell
              key={day.dateStr}
              day={day}
              variant={variant}
              heatmapQuartiles={heatmapQuartiles}
              onClick={day.isCurrentMonth && day.amount > 0 ? () => handleDayClick(day.dateStr) : undefined}
            />
          ))}
        </div>

        {/* Monthly Total */}
        <div className="pt-1 text-center text-sm text-muted-foreground">
          合計{' '}
          <span className="font-semibold text-foreground">
            {formatAmount(monthTotal)}
          </span>
        </div>
      </CardContent>
    </Card>

    <DailySpendingSheet
      open={sheetOpen}
      onOpenChange={(open) => { if (!open) setSelectedDateStr(null) }}
      transactions={selectedDayTransactions}
      selectedDateStr={selectedDateStr}
    />
    </>
  )
}
